<script setup lang="ts">
import type { Component } from "vue";
import { onMounted, onUnmounted, reactive, watch } from "vue";
import type { ModalEntry, ModalPosition } from "../stores/modalStore.ts";
import { useModalStore } from "../stores/modalStore.ts";

const modalStore = useModalStore();

const MIN_WIDTH = 200;
const MIN_HEIGHT = 80;

interface ModalRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const rects = reactive<Record<number, ModalRect>>({});

function initialRect(
  width: number,
  height: number,
  position?: ModalPosition
): ModalRect {
  if (position) return { x: position.x, y: position.y, width, height };
  return {
    x: Math.max(0, Math.round((globalThis.innerWidth - width) / 2)),
    y: Math.max(0, Math.round((globalThis.innerHeight - height) / 2)),
    width,
    height,
  };
}

watch(
  () => modalStore.stack.map((m) => m.id),
  (newIds, oldIds = []) => {
    for (const id of newIds) {
      if (!rects[id]) {
        const modal = modalStore.stack.find((m) => m.id === id);
        if (modal)
          rects[id] = initialRect(modal.width, modal.height, modal.position);
      }
    }
    const newSet = new Set(newIds);
    for (const id of oldIds) {
      if (!newSet.has(id)) Reflect.deleteProperty(rects, id);
    }
  },
  { immediate: true }
);

// ── Drag (event-delegated; only fires on .draggable / .movable elements) ────

let dragging: {
  id: number;
  startMouseX: number;
  startMouseY: number;
  startX: number;
  startY: number;
} | null = null;

function onFrameMouseDown(
  event: MouseEvent,
  id: number,
  modal: ModalEntry
): void {
  if (!modal.draggable) return;
  const target = event.target as HTMLElement;
  const handle = target.closest(".draggable, .movable");
  if (!handle) return;
  // Don't start a drag if the click landed on an interactive control inside the handle
  if (target.closest("button, a, input, select, textarea")) return;

  event.preventDefault();
  const rect = rects[id];
  if (!rect) return;
  dragging = {
    id,
    startMouseX: event.clientX,
    startMouseY: event.clientY,
    startX: rect.x,
    startY: rect.y,
  };
  document.body.style.cursor = "grabbing";
  document.body.style.userSelect = "none";
  document.addEventListener("mousemove", onDragMove);
  document.addEventListener("mouseup", onDragEnd);
}

function onDragMove(event: MouseEvent): void {
  if (!dragging) return;
  const rect = rects[dragging.id];
  if (!rect) return;
  rect.x = dragging.startX + (event.clientX - dragging.startMouseX);
  rect.y = dragging.startY + (event.clientY - dragging.startMouseY);
}

function onDragEnd(): void {
  dragging = null;
  document.body.style.cursor = "";
  document.body.style.userSelect = "";
  document.removeEventListener("mousemove", onDragMove);
  document.removeEventListener("mouseup", onDragEnd);
}

// ── Resize (8 custom hit-zones on the frame, outside overflow:hidden window) ─

type ResizeEdge = "n" | "ne" | "e" | "se" | "s" | "sw" | "w" | "nw";

const EDGE_CURSORS: Record<ResizeEdge, string> = {
  n: "n-resize",
  ne: "ne-resize",
  e: "e-resize",
  se: "se-resize",
  s: "s-resize",
  sw: "sw-resize",
  w: "w-resize",
  nw: "nw-resize",
};

let resizing: {
  id: number;
  edge: ResizeEdge;
  startMouseX: number;
  startMouseY: number;
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
} | null = null;

function onResizeStart(event: MouseEvent, id: number, edge: ResizeEdge): void {
  event.preventDefault();
  event.stopPropagation();
  const rect = rects[id];
  if (!rect) return;
  resizing = {
    id,
    edge,
    startMouseX: event.clientX,
    startMouseY: event.clientY,
    startX: rect.x,
    startY: rect.y,
    startWidth: rect.width,
    startHeight: rect.height,
  };
  document.body.style.cursor = EDGE_CURSORS[edge];
  document.body.style.userSelect = "none";
  document.addEventListener("mousemove", onResizeMove);
  document.addEventListener("mouseup", onResizeEnd);
}

function onResizeMove(event: MouseEvent): void {
  if (!resizing) return;
  const rect = rects[resizing.id];
  if (!rect) return;

  const dx = event.clientX - resizing.startMouseX;
  const dy = event.clientY - resizing.startMouseY;
  const edge = resizing.edge;

  let newX = resizing.startX;
  let newY = resizing.startY;
  let newW = resizing.startWidth;
  let newH = resizing.startHeight;

  if (edge === "e" || edge === "ne" || edge === "se") {
    newW = Math.max(MIN_WIDTH, resizing.startWidth + dx);
  }
  if (edge === "w" || edge === "nw" || edge === "sw") {
    const clampDx = Math.min(dx, resizing.startWidth - MIN_WIDTH);
    newW = resizing.startWidth - clampDx;
    newX = resizing.startX + clampDx;
  }
  if (edge === "s" || edge === "se" || edge === "sw") {
    newH = Math.max(MIN_HEIGHT, resizing.startHeight + dy);
  }
  if (edge === "n" || edge === "ne" || edge === "nw") {
    const clampDy = Math.min(dy, resizing.startHeight - MIN_HEIGHT);
    newH = resizing.startHeight - clampDy;
    newY = resizing.startY + clampDy;
  }

  rect.x = newX;
  rect.y = newY;
  rect.width = newW;
  rect.height = newH;
}

function onResizeEnd(): void {
  resizing = null;
  document.body.style.cursor = "";
  document.body.style.userSelect = "";
  document.removeEventListener("mousemove", onResizeMove);
  document.removeEventListener("mouseup", onResizeEnd);
}

// ── Escape (capture phase so focused children cannot swallow it) ─────────────

function onKeyDown(event: KeyboardEvent): void {
  if (event.key !== "Escape") return;
  const top = modalStore.stack.at(-1);
  if (top?.closeOnEscapeKeyPressed) {
    event.preventDefault();
    void handleClose(top);
  }
}

onMounted(() => {
  document.addEventListener("keydown", onKeyDown, true);
});

onUnmounted(() => {
  document.removeEventListener("mousemove", onDragMove);
  document.removeEventListener("mouseup", onDragEnd);
  document.removeEventListener("mousemove", onResizeMove);
  document.removeEventListener("mouseup", onResizeEnd);
  document.removeEventListener("keydown", onKeyDown, true);
});

// ── Styles ───────────────────────────────────────────────────────────────────

function getFrameStyle(
  modal: ModalEntry,
  index: number
): Record<string, string> {
  const rect = rects[modal.id];
  return {
    left: `${String(rect?.x ?? 0)}px`,
    top: `${String(rect?.y ?? 0)}px`,
    width: `${String(rect?.width ?? modal.width)}px`,
    height: `${String(rect?.height ?? modal.height)}px`,
    zIndex: String(10_001 + index * 2),
  };
}

// ── Close handlers ────────────────────────────────────────────────────────────

async function handleClose(modal: ModalEntry): Promise<void> {
  if (modal.onClose) await modal.onClose();
  modalStore.close();
}

async function handleMaskClick(modal: ModalEntry): Promise<void> {
  if (!modal.closeOnOverlayClick) return;
  if (modal.onOverlayClick) await modal.onOverlayClick();
  modalStore.close();
}
</script>

<template>
  <Teleport to="body">
    <!-- eslint-disable vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions, vue/no-v-html -->
    <template v-for="(modal, index) in modalStore.stack" :key="modal.id">
      <!-- Per-layer darkening mask. Blocks interaction below. -->
      <div
        class="modal-mask"
        :style="{ zIndex: 10000 + index * 2 }"
        @click="handleMaskClick(modal)"
      />

      <!-- Frame: position:fixed, sets size + position.
           No overflow so resize handles can straddle the border. -->
      <div
        class="modal-frame"
        :style="getFrameStyle(modal, index)"
        @mousedown="onFrameMouseDown($event, modal.id, modal)"
      >
        <!-- Resize hit-zones — siblings of the window, z-index above it.
             Each straddles the frame border (4px inside, 4px outside). -->
        <template v-if="modal.enableResizing">
          <div
            class="modal-resize modal-resize-n"
            @mousedown.prevent.stop="onResizeStart($event, modal.id, 'n')"
          />
          <div
            class="modal-resize modal-resize-s"
            @mousedown.prevent.stop="onResizeStart($event, modal.id, 's')"
          />
          <div
            class="modal-resize modal-resize-e"
            @mousedown.prevent.stop="onResizeStart($event, modal.id, 'e')"
          />
          <div
            class="modal-resize modal-resize-w"
            @mousedown.prevent.stop="onResizeStart($event, modal.id, 'w')"
          />
          <div
            class="modal-resize modal-resize-ne"
            @mousedown.prevent.stop="onResizeStart($event, modal.id, 'ne')"
          />
          <div
            class="modal-resize modal-resize-nw"
            @mousedown.prevent.stop="onResizeStart($event, modal.id, 'nw')"
          />
          <div
            class="modal-resize modal-resize-se"
            @mousedown.prevent.stop="onResizeStart($event, modal.id, 'se')"
          />
          <div
            class="modal-resize modal-resize-sw"
            @mousedown.prevent.stop="onResizeStart($event, modal.id, 'sw')"
          />
        </template>

        <!-- Window: fills the frame, overflow:hidden for border-radius clipping -->
        <div class="modal-window" role="dialog" aria-modal="true">
          <!-- Content renders everything — header, body, footer are its responsibility -->
          <div
            v-if="typeof modal.content === 'string'"
            v-html="modal.content"
          />
          <component
            :is="modal.content as Component"
            v-else
            v-bind="modal.componentProps"
          />
        </div>
      </div>
    </template>
    <!-- eslint-enable vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions, vue/no-v-html -->
  </Teleport>
</template>
